import {useEffect,useRef,useState} from 'react';
import {useCopy} from '@platform/ui/config';
import {attachLocalPreview} from './localPreview';
import {AntMediaPlayer} from './AntMediaPlayer';
import {validatePlaybackSource,type PlaybackSource,type PlaybackStatus,type PlaybackIssue} from './playback';
import config from './player.config.json';
import './broadcast-player.css';

export function BroadcastPlayer({source,poster,active=true,onStatus}:{source:PlaybackSource;poster?:string;active?:boolean;onStatus?:(status:PlaybackStatus)=>void}){
  return source.kind==='antmedia'?<AntMediaPlayer source={source} active={active} onStatus={onStatus}/>:<LocalPreviewPlayer source={source} poster={poster} active={active} onStatus={onStatus}/>;
}

// The custom HTML video below exists only for the local transport fixture.
function LocalPreviewPlayer({source,poster,active=true,onStatus}:{source:PlaybackSource;poster?:string;active?:boolean;onStatus?:(status:PlaybackStatus)=>void}){
  const t=useCopy(),videoRef=useRef<HTMLVideoElement>(null),callback=useRef(onStatus);
  callback.current=onStatus;
  const [status,setStatus]=useState<PlaybackStatus>('idle'),[error,setError]=useState(''),[restart,setRestart]=useState(0);
  const [muted,setMuted]=useState(true);
  // Identical descriptors on a new game snapshot must not recreate the connection.
  const identity=JSON.stringify(source),previewLabel=useRef('');
  previewLabel.current=t('broadcast.previewLabel');
  useEffect(()=>{
    const video=videoRef.current;if(!video)return;
    let disposed=false,attempt=0,generation=0,controller:AbortController|undefined,transportStop:(()=>void)|undefined;
    let retryTimer=0,watchdog=0,expiryTimer=0,frameRequest=0,lastProgress=performance.now(),lastTime=-1,hasFrame=false,failed=false;
    const current=JSON.parse(identity) as PlaybackSource;
    const update=(value:PlaybackStatus)=>{if(!disposed){setStatus(value);callback.current?.(value);}};
    const clearTransport=()=>{
      controller?.abort();transportStop?.();transportStop=undefined;
      clearInterval(watchdog);clearTimeout(expiryTimer);
      if(frameRequest)video.cancelVideoFrameCallback?.(frameRequest);
      video.pause();(video.srcObject as MediaStream|null)?.getTracks().forEach(track=>track.stop());
      video.srcObject=null;video.removeAttribute('src');video.load();
    };
    const fail=(problem:PlaybackIssue)=>{
      if(disposed||failed)return;failed=true;clearTransport();
      setError(problem.code);
      if(problem.code==='expired'){update('expired');return;}
      if(problem.retryable&&attempt<config.maximumRetries){
        update('retrying');retryTimer=window.setTimeout(start,config.retryBaseMs*2**attempt++);
      }else update('error');
    };
    const watchFrame=()=>{
      if(disposed||failed)return;
      lastProgress=performance.now();hasFrame=true;
      frameRequest=video.requestVideoFrameCallback(watchFrame);
    };
    const start=()=>{
      if(disposed)return;
      clearTransport();failed=false;hasFrame=false;lastTime=-1;lastProgress=performance.now();setError('');
      const invalid=validatePlaybackSource(current);if(invalid){fail(invalid);return;}
      update('connecting');controller=new AbortController();const signal=controller.signal,run=++generation;
      if('expiresAtUtc' in current&&current.expiresAtUtc)expiryTimer=window.setTimeout(()=>fail({code:'expired',retryable:false}),Math.min(2147483647,Date.parse(current.expiresAtUtc)-Date.now()));
      if(video.requestVideoFrameCallback)frameRequest=video.requestVideoFrameCallback(watchFrame);
      watchdog=window.setInterval(()=>{
        if(disposed||failed||document.hidden)return;
        if(!video.requestVideoFrameCallback&&video.currentTime!==lastTime){lastTime=video.currentTime;lastProgress=performance.now();hasFrame=video.readyState>=2;}
        if(video.paused&&video.readyState>=2)return;
        if(performance.now()-lastProgress>(hasFrame?config.stallTimeoutMs:config.connectionTimeoutMs))fail({code:'connection',retryable:true});
      },1000);
      void attachLocalPreview(video,()=>previewLabel.current,signal)
        .then(stop=>{if(disposed||signal.aborted||run!==generation)stop();else transportStop=stop;})
        .catch(()=>{if(!signal.aborted&&run===generation)fail({code:'connection',retryable:true});});
    };
    const play=()=>{void video.play().catch(()=>{if(!disposed&&!failed)update('blocked');});};
    const playing=()=>{if(!failed){hasFrame=true;lastProgress=performance.now();update('playing');}};
    const waiting=()=>{if(!failed&&hasFrame)update('buffering');};
    const mediaError=()=>{if(!failed)fail({code:'connection',retryable:true});};
    const ended=()=>{if(!failed)fail({code:'connection',retryable:true});};
    video.addEventListener('loadedmetadata',play);video.addEventListener('playing',playing);video.addEventListener('waiting',waiting);video.addEventListener('error',mediaError);video.addEventListener('ended',ended);
    if(active)start();else update('idle');
    return()=>{disposed=true;++generation;clearTimeout(retryTimer);clearTransport();video.removeEventListener('loadedmetadata',play);video.removeEventListener('playing',playing);video.removeEventListener('waiting',waiting);video.removeEventListener('error',mediaError);video.removeEventListener('ended',ended);};
  },[identity,active,restart]);
  return <div className="broadcast-player" data-ui="broadcast-player" data-transport={source.kind} data-playback-status={status}>
    <video ref={videoRef} poster={poster} autoPlay playsInline muted={muted} controls controlsList="nodownload" onVolumeChange={event=>setMuted(event.currentTarget.muted)} aria-label={t('broadcast.video')}/>
    {status!=='playing'&&<div className="broadcast-player-message" role="status"><span>{t(error?'broadcast.error.'+error:'broadcast.status.'+status)}</span>
      {status==='blocked'&&<button type="button" onClick={()=>void videoRef.current?.play().catch(()=>{})}>{t('broadcast.start')}</button>}
      {status==='error'&&<button type="button" onClick={()=>setRestart(value=>value+1)}>{t('broadcast.retry')}</button>}
    </div>}
  </div>;
}
